import { toVignettePlacement, VignettePlacement } from './compute-vignette-image-placement';
import { ContainerRect, FaceRelativeBbox, FaceWithRelativeBbox, ImageRect } from './face-centering';
import { fitRect } from './fit-rect';

/**
 * «Кадрирование портрета» личного разворота: лицо занимает треть кадра, четверть
 * кадра, либо кадр остаётся как снят. Настройка уровня дизайна — применяется ко
 * всем OWNER-рамкам личных разворотов проекта при генерации.
 */
export type PortraitCropMode = 'third' | 'quarter' | 'asShot';

/** Дальше этого зума пиксели портрета уже видны в печати. */
const MAX_PORTRAIT_CROP_ZOOM = 4;
/** Центр лица по вертикали — классическая портретная посадка с воздухом над головой. */
const FACE_CENTER_VERTICAL_ANCHOR = 0.4;

export interface PortraitCropInput {
  /** Лица кадра (relativeBbox в долях исходника). */
  faces: readonly FaceWithRelativeBbox[] | undefined;
  container: ContainerRect;
  /** Текущее cover-fit положение изображения в координатах контейнера. */
  image: ImageRect;
  mode: PortraitCropMode;
}

/**
 * Возвращает новое положение изображения, при котором высота лица равна доле
 * контейнера (`third` → 1/3, `quarter` → 1/4), лицо по горизонтали в центре,
 * по вертикали — с воздухом над головой. Изображение никогда не сжимается ниже
 * cover-fit и не двигается так, чтобы открыть край контейнера.
 * `asShot`, отсутствие лиц или вырожденный bbox — позиция не меняется (null).
 */
export function calculatePortraitCropRect(input: PortraitCropInput): ImageRect | null {
  const { faces, container, image, mode } = input;
  if (mode === 'asShot') return null;
  const face = pickLargestFace(faces);
  if (!face) return null;

  const rule: PortraitCropRule = {
    faceHeightFrac: mode === 'third' ? 1 / 3 : 1 / 4,
    faceCenterXFrac: 0.5,
    faceCenterYFrac: FACE_CENTER_VERTICAL_ANCHOR,
  };

  return cropRectForRule(rule, container, image, face.relativeBbox, MAX_PORTRAIT_CROP_ZOOM)?.rect ?? null;
}

/**
 * «Где лицо в рамке» — доли ЯЧЕЙКИ, не картинки: доли от размещённого изображения
 * инвариантны к трансформу и ничего не кодируют.
 */
export interface PortraitCropRule {
  /** Высота лица в долях высоты ячейки. */
  faceHeightFrac: number;
  /** Центр лица по горизонтали в долях ширины ячейки. */
  faceCenterXFrac: number;
  /** Центр лица по вертикали в долях высоты ячейки. */
  faceCenterYFrac: number;
}

/** Потолок зума трансформа: `vignette-transform.dto.ts` `@Min(1) @Max(2.5)`. */
const MAX_RULE_ZOOM = 2.5;

/**
 * Bbox эндпоинта лиц — квадрат головы с волосами и шеей; само лицо (линия волос — подбородок) ≈ 0,55 его
 * высоты. Пресет кадра задаёт долю лица, поэтому в ячейке ему нужен bbox в 1,8 раза выше.
 */
// ponytail: one ratio for every detector box (11Б, 6 portraits: face 0.53–0.59 of the box);
// per-face landmarks if the detector changes.
const DETECTOR_BOX_PER_FACE = 1.8;

/** Отражения рамки: рендер зеркалит картинку внутри её rect, и лицо с центром cx стоит на экране в 1 − cx. */
export interface PortraitCropFlip {
  horizontal?: boolean;
  vertical?: boolean;
}

/** Bbox лица глазами зрителя — с учётом отражений рамки. */
function flippedFaceBbox(bbox: FaceRelativeBbox, flip: PortraitCropFlip | undefined): FaceRelativeBbox {
  if (!flip?.horizontal && !flip?.vertical) return bbox;
  return {
    x1: flip.horizontal ? 1 - bbox.x2 : bbox.x1,
    x2: flip.horizontal ? 1 - bbox.x1 : bbox.x2,
    y1: flip.vertical ? 1 - bbox.y2 : bbox.y1,
    y2: flip.vertical ? 1 - bbox.y1 : bbox.y2,
  };
}

/**
 * Ядро кадрирования: положение изображения, при котором лицо занимает заданную долю
 * ячейки и его центр стоит в заданных долях. `image` — размер cover-fit картинки в
 * координатах контейнера. `reached: false` — зум упёрся в пол cover-fit или в потолок,
 * и лицо в рамке другого размера, чем требует правило.
 */
function cropRectForRule(
  rule: PortraitCropRule,
  container: ContainerRect,
  image: ContainerRect,
  rel: FaceRelativeBbox,
  maxZoom: number,
  headroom = true,
): { rect: ImageRect; reached: boolean } | null {
  const relHeight = rel.y2 - rel.y1;
  const relWidth = rel.x2 - rel.x1;
  if (relHeight <= 0 || relWidth <= 0) return null;

  const currentFaceHeight = relHeight * image.height;
  if (currentFaceHeight <= 0) return null;

  const neededZoom = (rule.faceHeightFrac * container.height) / currentFaceHeight;
  // Ниже cover-fit не опускаемся: рамка всегда закрыта фотографией целиком.
  const minCoverZoom = Math.max(container.width / image.width, container.height / image.height, 0);
  let zoom = Math.min(Math.max(neededZoom, minCoverZoom, 0), maxZoom);
  let wantedZoom = neededZoom;

  // Воздух над головой не меньше, чем у образца. Картинка упёрлась в верх ячейки, а
  // воздуха меньше — добираем зумом. Под потолком не добрать — голова у края самого фото:
  // как снято, лицо остаётся размером образца.
  const faceTopFrac = rule.faceCenterYFrac - rule.faceHeightFrac / 2;
  if (headroom && rel.y1 > 0 && faceTopFrac * container.height > rel.y1 * image.height * zoom) {
    const headroomZoom = (faceTopFrac * container.height) / (rel.y1 * image.height);
    if (headroomZoom <= maxZoom) {
      wantedZoom = headroomZoom;
      zoom = headroomZoom;
    }
  }

  // Допуск 1 %: шум округлений на самом образце не должен читаться как промах.
  const reached = neededZoom >= minCoverZoom * (1 - ZOOM_REACH_TOLERANCE) && wantedZoom <= maxZoom * (1 + ZOOM_REACH_TOLERANCE);

  const width = image.width * zoom;
  const height = image.height * zoom;

  const faceCenterX = (rel.x1 + relWidth / 2) * width;
  const faceCenterY = (rel.y1 + relHeight / 2) * height;
  const headroomTop = faceTopFrac * container.height - rel.y1 * height;

  const left = clamp(container.width * rule.faceCenterXFrac - faceCenterX, container.width - width, 0);
  const top = clamp(Math.max(container.height * rule.faceCenterYFrac - faceCenterY, headroomTop), container.height - height, 0);

  return { rect: { left, top, width, height }, reached };
}

const ZOOM_REACH_TOLERANCE = 0.01;

function coverRect(cellSize: ContainerRect, imageSize: ContainerRect): ContainerRect {
  return fitRect(imageSize.width, imageSize.height, cellSize, { mode: 'cover', round: false });
}

/**
 * Снимает правило с образца: раскладывает изображение по трансформу образца (обратная
 * `toVignettePlacement`) и меряет лицо относительно ячейки.
 */
export function portraitCropRuleFromSample(
  transform: VignettePlacement,
  cellSize: ContainerRect,
  imageSize: ContainerRect,
  face: FaceRelativeBbox,
  flip?: PortraitCropFlip,
): PortraitCropRule {
  const faceBbox = flippedFaceBbox(face, flip);
  const cover = coverRect(cellSize, imageSize);
  const width = cover.width * transform.zoom;
  const height = cover.height * transform.zoom;
  const left = cellSize.width / 2 - transform.focusX * width;
  const top = cellSize.height / 2 - transform.focusY * height;

  return {
    faceHeightFrac: ((faceBbox.y2 - faceBbox.y1) * height) / cellSize.height,
    faceCenterXFrac: (left + ((faceBbox.x1 + faceBbox.x2) / 2) * width) / cellSize.width,
    faceCenterYFrac: (top + ((faceBbox.y1 + faceBbox.y2) / 2) * height) / cellSize.height,
  };
}

/**
 * Кладёт лицо портрета туда, где оно стоит у образца. Зум зажат в [1; 2,5]: лицо мелкое —
 * зум упирается в потолок 2,5×, рамка всё равно кадрируется; лицо крупнее правила — зум 1.
 * Лица нет — undefined. Отражения рамки правило не несёт (их копирует вызывающий), но математика их учитывает.
 * `sampleHeadroom` — правило снято с образца (меряно по bbox): зум добирает воздух над головой как у него.
 * Пресет кадра передаёт false: лицо (не bbox) занимает ровно долю пресета, воздух не добирается.
 * `reached: false` — лицо не дотянулось до образца (зум упёрся в 1 или в 2,5); в трансформу поле не входит.
 */
export function applyPortraitCropRule(
  rule: PortraitCropRule,
  cellSize: ContainerRect,
  imageSize: ContainerRect,
  faceBbox: FaceRelativeBbox | undefined,
  flip?: PortraitCropFlip,
  sampleHeadroom = true,
): (VignettePlacement & { reached: boolean }) | undefined {
  if (!faceBbox) return undefined;

  const face = flippedFaceBbox(faceBbox, flip);
  const boxRule = sampleHeadroom ? rule : { ...rule, faceHeightFrac: rule.faceHeightFrac * DETECTOR_BOX_PER_FACE };
  const crop = cropRectForRule(boxRule, cellSize, coverRect(cellSize, imageSize), face, MAX_RULE_ZOOM, sampleHeadroom);
  if (!crop) return undefined;

  const { zoom, focusX, focusY } = toVignettePlacement(cellSize, imageSize.width, imageSize.height, crop.rect);

  return {
    zoom: clamp(zoom, 1, MAX_RULE_ZOOM),
    focusX: clamp(focusX, 0, 1),
    focusY: clamp(focusY, 0, 1),
    reached: crop.reached,
  };
}

/** Bbox пригоден для геометрии: все четыре доли — конечные числа (`Infinity`/`NaN` из JSON ловятся здесь). */
export function isFiniteFaceBbox(bbox: FaceRelativeBbox | null | undefined): bbox is FaceRelativeBbox {
  return !!bbox && [bbox.x1, bbox.y1, bbox.x2, bbox.y2].every(v => Number.isFinite(v));
}

/** Лицо кадра по умолчанию — самое крупное: привязки к персоне у кадра может не быть. */
export function pickLargestFace(faces: readonly FaceWithRelativeBbox[] | undefined): FaceWithRelativeBbox | null {
  let best: FaceWithRelativeBbox | null = null;
  let bestArea = 0;
  for (const face of faces ?? []) {
    const b = face?.relativeBbox;
    if (!isFiniteFaceBbox(b)) continue;
    const area = (b.x2 - b.x1) * (b.y2 - b.y1);
    if (area > bestArea) {
      bestArea = area;
      best = face;
    }
  }

  return best;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
